import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { User, Mail, Phone, Lock, Check, AlertCircle } from 'lucide-react';

const Settings = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Password change form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      setMessage({ type: 'error', text: 'Password must contain uppercase, lowercase, and numbers' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to change password' 
      });
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '32px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
    },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: `2px solid ${theme.colors.border}`,
    },
    tab: (isActive) => ({
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '600',
      color: isActive ? theme.colors.primary : theme.colors.textSecondary,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: `2px solid ${isActive ? theme.colors.primary : 'transparent'}`,
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '-2px',
    }),
    content: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: '24px',
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '16px',
    },
    infoGrid: {
      display: 'grid',
      gap: '16px',
    },
    infoItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      backgroundColor: theme.colors.backgroundSecondary,
      borderRadius: '8px',
      border: `1px solid ${theme.colors.border}`,
    },
    infoLabel: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      marginBottom: '4px',
    },
    infoValue: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    message: (type) => ({
      padding: '12px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      backgroundColor: type === 'success' ? theme.colors.success + '15' : theme.colors.danger + '15',
      color: type === 'success' ? theme.colors.success : theme.colors.danger,
      border: `1px solid ${type === 'success' ? theme.colors.success : theme.colors.danger}`,
    }),
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: theme.colors.primary + '20',
      color: theme.colors.primary,
    },
    passwordRequirements: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      padding: '12px',
      backgroundColor: theme.colors.backgroundSecondary,
      borderRadius: '8px',
      marginTop: '-8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.tabs}>
        <button
          style={styles.tab(activeTab === 'profile')}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          style={styles.tab(activeTab === 'security')}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
      </div>

      {activeTab === 'profile' && (
        <div style={styles.content}>
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Profile Information</h2>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <User size={20} color={theme.colors.primary} />
                    <div style={{ flex: 1 }}>
                      <div style={styles.infoLabel}>Name</div>
                      <div style={styles.infoValue}>{user?.name}</div>
                    </div>
                  </div>

                  <div style={styles.infoItem}>
                    <Mail size={20} color={theme.colors.primary} />
                    <div style={{ flex: 1 }}>
                      <div style={styles.infoLabel}>Email</div>
                      <div style={styles.infoValue}>{user?.email}</div>
                    </div>
                  </div>

                  {user?.phone && (
                    <div style={styles.infoItem}>
                      <Phone size={20} color={theme.colors.primary} />
                      <div style={{ flex: 1 }}>
                        <div style={styles.infoLabel}>Phone</div>
                        <div style={styles.infoValue}>{user.phone}</div>
                      </div>
                    </div>
                  )}

                  {user?.role !== 'USER' && (
                    <div style={styles.infoItem}>
                      <User size={20} color={theme.colors.primary} />
                      <div style={{ flex: 1 }}>
                        <div style={styles.infoLabel}>Role</div>
                        <div style={styles.infoValue}>
                          <span style={styles.badge}>
                            {user?.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'security' && (
        <div style={styles.content}>
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Change Password</h2>
                
                {message.text && (
                  <div style={styles.message(message.type)}>
                    {message.type === 'success' ? (
                      <Check size={18} />
                    ) : (
                      <AlertCircle size={18} />
                    )}
                    {message.text}
                  </div>
                )}

                <form onSubmit={handleChangePassword} style={styles.form}>
                  <Input
                    type="password"
                    label="Current Password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    placeholder="Enter your current password"
                    icon={Lock}
                    required
                  />

                  <Input
                    type="password"
                    label="New Password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    placeholder="Enter your new password"
                    icon={Lock}
                    required
                  />

                  <div style={styles.passwordRequirements}>
                    <strong>Password requirements:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>At least 6 characters long</li>
                      <li>Contains at least one uppercase letter</li>
                      <li>Contains at least one lowercase letter</li>
                      <li>Contains at least one number</li>
                    </ul>
                  </div>

                  <Input
                    type="password"
                    label="Confirm New Password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    placeholder="Confirm your new password"
                    icon={Lock}
                    required
                  />

                  <Button type="submit" disabled={loading}>
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Settings;
